import React, { useState } from 'react';
import { Table, Spinner, Header } from '@cloudscape-design/components';

import { InstancesTableConfig } from './table.config';
import Button from '@cloudscape-design/components/button';
import { useNavigate } from 'react-router-dom';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { InstanceType } from '../../../types/instance';
import { useApiWithFlash } from '../../../utils/hooks/useApiWithFlash';

export interface InstancesTableProps {
  isLoading: boolean;
  instances: InstanceType[];
  error: any;
  onRefresh: () => void;
}

export const InstancesTable: React.FC<InstancesTableProps> = ({
  isLoading,
  instances = [],
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { handleApiWithFlashInput } = useApiWithFlash();
  const [selectedInstance, setSelectedInstance] = useState<InstanceType | null>(null);

  const handleCreateInstanceClick = () => {
    navigate('/instances/create', { state: { instances } });
  };

  const handleDelete = async () => {
    if (!selectedInstance) return;
    const response = await handleApiWithFlashInput(`/instances/${selectedInstance.instanceId}`, 'DELETE', {
      confirmationText: selectedInstance.instanceName,
      deleteMessage: `Are you sure you want to delete instance "${selectedInstance.instanceName}"? This will remove all associated data and roles, and cannot be undone.`,
      successMessage: `Instance "${selectedInstance.instanceName}" deletion initiated`,
      errorMessage: `Error deleting instance "${selectedInstance.instanceName}"`,
      mutateKey: '/instances',
    });
    if (response?.success) {
      setSelectedInstance(null);
    }
  };

  return (
    <Table
      items={instances}
      loading={isLoading}
      loadingText="Loading instances"
      selectionType="single"
      selectedItems={selectedInstance ? [selectedInstance] : []}
      onSelectionChange={({ detail }) => setSelectedInstance(detail.selectedItems[0] ?? null)}
      header={
        <Header
          variant="awsui-h1-sticky"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button iconName="refresh" ariaLabel="Refresh" onClick={onRefresh} />
              <Button disabled={!selectedInstance} onClick={handleDelete}>
                Delete Instance
              </Button>
              <Button variant="primary" onClick={handleCreateInstanceClick}>
                Create Instance
              </Button>
            </SpaceBetween>
          }
        >
          Instances {instances && !isLoading ? `(${instances.length})` : <Spinner />}
        </Header>
      }
      columnDefinitions={InstancesTableConfig}
      trackBy="instanceId"
    />
  );
};
